import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { getSaudiWorkCityOptions } from '../pointage/pointageHelpers';
import { getPerDiemRate } from './utils/payrollSaudiFormat';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  Filter, Plus, X, Truck, Search, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle,
  ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, FileText, Eye,
  LayoutGrid, Layers, Shield,
  DollarSign, MapPin, ClipboardCheck, Clock, XCircle, Info, Calendar, User, Settings,
} from 'lucide-react';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import {
  SalairesListControlsStrip,
  useClearSearchOnTableView,
  renderSalairesBulkDeleteFilterAction,
  useSalairesTableSelection,
  SALAIRES_FORM_GRID_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import '../../core/styles/global-dropdowns.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const MissionsDeplacementsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.missionsDeplacementsPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const dateLocale = getBrandLocale(i18n.language);

  const SAUDI_CITY_OPTIONS = getSaudiWorkCityOptions();

  const MISSION_TYPE_OPTIONS = [
    { value: 'training', label: p('type_training') },
    { value: 'meeting', label: p('type_meeting') },
    { value: 'audit', label: p('type_audit') },
    { value: 'prospecting', label: p('type_prospecting') },
    { value: 'other', label: p('type_other') },
  ];
  const DEPLACEMENT_TYPE_OPTIONS = [
    { value: 'personal_vehicle', label: p('travel_personal_vehicle') },
    { value: 'company_vehicle', label: p('travel_company_vehicle') },
    { value: 'public_transport', label: p('travel_public_transport') },
  ];

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return p('not_available');
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('not_available');
    return formatBrandMoney(n);
  };

  const fmtDate = (val) => (val ? new Date(val).toLocaleDateString(dateLocale) : p('not_available'));

  const DESTINATION_OPTIONS = [
    ...SAUDI_CITY_OPTIONS,
    { value: 'international', label: p('destination_international') },
  ];

  const unwrapApiList = (response) => {
    if (Array.isArray(response)) return response;
    if (response?.success && Array.isArray(response.data)) return response.data;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const [activeTab, setActiveTab] = useState('missions');
  const entityLabel = () => (activeTab === 'missions'
    ? p('title_missions', { defaultValue: 'مهمة' })
    : p('title_travel', { defaultValue: 'تنقل' }));
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useClearSearchOnTableView(viewMode, searchTerm, setSearchTerm);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_items');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();

  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formTab, setFormTab] = useState('info');

  const missionFormTabs = useMemo(() => ([
    { id: 'info', label: p('section_info'), icon: User, iconClassName: 'text-blue-400' },
    { id: 'details', label: p('section_allowances'), icon: DollarSign, iconClassName: 'text-green-400' },
  ]), [p]);

  const [formData, setFormData] = useState({
    employeeId: '',
    titre: '',
    type: '',
    destination: '',
    dateDebut: '',
    dateFin: '',
    distance: '',
    forfaitJournalier: '',
    transport: '',
    autresIndemnites: '',
    montantIndemnite: '',
    typeVehicule: '',
    status: '',
    reason: '',
    notes: '',
  });

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showModal && !showDetailsModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showModal, showDetailsModal]);
  
  // Statuts des missions (alignés sur la colonne SQLite `status`)
  const statutsMission = [
    { id: 'planned', label: p('status_planned_f'), color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { id: 'in_progress', label: p('status_in_progress'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { id: 'completed', label: p('status_completed_f'), color: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { id: 'cancelled', label: p('status_cancelled_f'), color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { id: 'validated', label: p('status_validated_f'), color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  ];
  
  // État pour les missions et déplacements (fetched from API)
  const [missions, setMissions] = useState([]);
  const [deplacements, setDeplacements] = useState([]);
  const { employees } = useSalairesEmployeeOptions();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [missionsRes, travelRes] = await Promise.all([
        localApiAdapter.get('/salaries/missions-travel/missions'),
        localApiAdapter.get('/salaries/missions-travel/travel'),
      ]);

      setMissions(sortRecordsNewestFirst(unwrapApiList(missionsRes)));
      setDeplacements(sortRecordsNewestFirst(unwrapApiList(travelRes)));
    } catch (error) {
      console.error('Error fetching missions/travel:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatutInfo = (id) => {
    return (
      statutsMission.find((s) => s.id === id) || {
        id: id || '',
        label: id || '—',
        color: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
      }
    );
  };

  const isMissionTab = activeTab === 'missions';
  const currentData = isMissionTab ? missions : deplacements;

  const statsGlobal = {
    totalMissions: missions.length,
    totalDeplacements: deplacements.length,
    totalEnCours: missions.filter((m) => m.status === 'in_progress' || m.status === 'planned').length,
    totalTerminees: missions.filter((m) => m.status === 'completed' || m.status === 'validated').length,
    montantTotal: missions.reduce((sum, m) => sum + (m.total_allowance || 0), 0) + deplacements.reduce((sum, d) => sum + (d.allowance_amount || 0), 0),
  };

  const missionStatusOptions = [
    { value: 'planned', label: p('status_planned_f'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'in_progress', label: p('status_in_progress'), icon: RefreshCcw, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'completed', label: p('status_completed_f'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled_f'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'validated', label: p('status_validated_f'), icon: CheckCircle, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  ];

  const deplacementStatusOptions = [
    { value: 'completed', label: p('status_completed_m'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'planned', label: p('status_planned_m'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'cancelled', label: p('status_cancelled_m'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const statusOptions = isMissionTab ? missionStatusOptions : deplacementStatusOptions;

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || (isMissionTab ? getStatutInfo(status) : { label: status || '—', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' });
    const Icon = config.icon || Info;
    const style = config.style || config.color || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        {config.icon ? <Icon size={14} /> : null}{config.label}
      </span>
    );
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const endpoint = isMissionTab ? 'missions' : 'travel';
      const item = currentData.find((i) => i.id === recordId);
      if (!item) return;
      await localApiAdapter.put(`/salaries/missions-travel/${endpoint}/${recordId}`, { ...item, status: newStatus });
      await fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const mainTabs = [
    { id: 'all_items', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_type', name: p('tab_by_type'), icon: Layers },
    { id: 'par_indemnite', name: p('tab_by_allowance'), icon: DollarSign },
  ];

  const quickFilterButtons = isMissionTab
    ? [
        { id: 'all', name: p('filter_all'), icon: Layers },
        { id: 'planned', name: p('status_planned_f_plural'), icon: Clock },
        { id: 'in_progress', name: p('status_in_progress'), icon: RefreshCcw },
        { id: 'completed', name: p('status_completed_f_plural'), icon: CheckCircle },
      ]
    : [
        { id: 'all', name: p('filter_all'), icon: Layers },
        { id: 'completed', name: p('status_completed_m_plural'), icon: CheckCircle },
        { id: 'planned', name: p('status_planned_m_plural'), icon: Clock },
      ];

  const getTypeDistribution = () => {
    const options = isMissionTab ? MISSION_TYPE_OPTIONS : DEPLACEMENT_TYPE_OPTIONS;
    return options.map((opt, i) => ({
      filterKey: opt.value,
      name: opt.label,
      count: currentData.filter((item) => item.type === opt.value || item.travel_type === opt.value).length,
      color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-green-500 to-green-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-orange-500 to-orange-600', 'bg-gradient-to-r from-yellow-500 to-yellow-600'][i % 5],
    }));
  };

  const itemDistribution = {
    all_items: {
      title: isMissionTab ? p('overview_missions') : p('overview_travel'),
      data: isMissionTab
        ? [
            { filterKey: 'planned', name: p('status_planned_f_plural'), count: missions.filter((m) => m.status === 'planned').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
            { filterKey: 'in_progress', name: p('status_in_progress'), count: missions.filter((m) => m.status === 'in_progress').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
            { filterKey: 'completed', name: p('status_completed_f_plural'), count: missions.filter((m) => m.status === 'completed').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
            { filterKey: 'validated', name: p('status_validated_f_plural'), count: missions.filter((m) => m.status === 'validated').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
          ]
        : [
            { filterKey: 'completed', name: p('status_completed_m_plural'), count: deplacements.filter((d) => d.status === 'completed').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
            { filterKey: 'planned', name: p('status_planned_m_plural'), count: deplacements.filter((d) => d.status === 'planned').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
            { filterKey: 'cancelled', name: p('status_cancelled_m_plural'), count: deplacements.filter((d) => d.status === 'cancelled').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
            { filterKey: 'other', name: p('status_other'), count: deplacements.filter((d) => !['completed', 'planned', 'cancelled'].includes(d.status)).length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
          ],
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: isMissionTab
        ? [
            { filterKey: 'planned', name: p('status_planned_f_plural'), count: missions.filter((m) => m.status === 'planned').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
            { filterKey: 'in_progress', name: p('status_in_progress'), count: missions.filter((m) => m.status === 'in_progress').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
            { filterKey: 'completed', name: p('status_completed_f_plural'), count: missions.filter((m) => m.status === 'completed').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
            { filterKey: 'validated', name: p('status_validated_f_plural'), count: missions.filter((m) => m.status === 'validated').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
            { filterKey: 'cancelled', name: p('status_cancelled_f_plural'), count: missions.filter((m) => m.status === 'cancelled').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
          ]
        : [
            { filterKey: 'completed', name: p('status_completed_m_plural'), count: deplacements.filter((d) => d.status === 'completed').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
            { filterKey: 'planned', name: p('status_planned_m_plural'), count: deplacements.filter((d) => d.status === 'planned').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
            { filterKey: 'cancelled', name: p('status_cancelled_m_plural'), count: deplacements.filter((d) => d.status === 'cancelled').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
          ],
    },
    par_type: { title: p('distribution_by_type'), data: getTypeDistribution() },
    par_indemnite: {
      title: p('distribution_by_allowance'),
      data: isMissionTab
        ? [
            { filterKey: 'lt_500', name: p('allowance_lt_500'), count: missions.filter((m) => (parseFloat(m.total_allowance) || 0) < 500).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
            { filterKey: '500_2000', name: p('allowance_500_2000'), count: missions.filter((m) => { const a = parseFloat(m.total_allowance) || 0; return a >= 500 && a < 2000; }).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
            { filterKey: '2000_5000', name: p('allowance_2000_5000'), count: missions.filter((m) => { const a = parseFloat(m.total_allowance) || 0; return a >= 2000 && a < 5000; }).length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
            { filterKey: 'gt_5000', name: p('allowance_gt_5000'), count: missions.filter((m) => (parseFloat(m.total_allowance) || 0) >= 5000).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
          ]
        : [
            { filterKey: 'lt_300', name: p('allowance_lt_300'), count: deplacements.filter((d) => (parseFloat(d.allowance_amount) || 0) < 300).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
            { filterKey: '300_1000', name: p('allowance_300_1000'), count: deplacements.filter((d) => { const a = parseFloat(d.allowance_amount) || 0; return a >= 300 && a < 1000; }).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
            { filterKey: '1000_3000', name: p('allowance_1000_3000'), count: deplacements.filter((d) => { const a = parseFloat(d.allowance_amount) || 0; return a >= 1000 && a < 3000; }).length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
            { filterKey: 'gt_3000', name: p('allowance_gt_3000'), count: deplacements.filter((d) => (parseFloat(d.allowance_amount) || 0) >= 3000).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
          ],
    },
  };

  const distributionInfo = itemDistribution[mainViewMode] || itemDistribution.all_items;

  const getItemDate = (item) => (isMissionTab ? item.start_date : item.date);

  const columnFilterRules = useMemo(() => {
    if (isMissionTab) {
      return [
        textColumnRule('employee_name', (row) => `${row.employee_name || ''} ${row.employee_mat || ''}`.trim()),
        textColumnRule('title', (row) => row.title || ''),
        textColumnRule('destination', (row) => row.destination || ''),
        textColumnRule('start_date', (row) => `${fmtDate(row.start_date)} ${fmtDate(row.end_date)}`.trim()),
        textColumnRule('total_allowance', (row) => String(row.total_allowance ?? '')),
        exactColumnRule('status', (row) => row.status || ''),
      ];
    }
    return [
      textColumnRule('employee_name', (row) => `${row.employee_name || ''} ${row.employee_mat || ''}`.trim()),
      textColumnRule('title', (row) => fmtDate(row.date)),
      textColumnRule('destination', (row) => row.destination || ''),
      textColumnRule('distance', (row) => String(row.distance ?? '')),
      textColumnRule('allowance_amount', (row) => String(row.allowance_amount ?? '')),
    ];
  }, [isMissionTab, dateLocale]);

  const colHeaderFilterProps = (key, label, extra = {}) => ({
    columnKey: key,
    label,
    value: columnFilters[key] ?? '',
    onChange: (v) => setColumnFilter(key, v),
    editingKey: columnEditingKey,
    onEditingKeyChange: setColumnEditingKey,
    thClassName: 'text-white font-semibold',
    ...extra,
  });

  const filteredData = useMemo(() => {
    const base = currentData.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      if (searchTerm) {
        const matches = isMissionTab
          ? item.employee_name?.toLowerCase().includes(searchLower) || item.employee_mat?.toLowerCase().includes(searchLower) || item.title?.toLowerCase().includes(searchLower) || item.destination?.toLowerCase().includes(searchLower)
          : item.employee_name?.toLowerCase().includes(searchLower) || item.employee_mat?.toLowerCase().includes(searchLower) || item.destination?.toLowerCase().includes(searchLower) || item.reason?.toLowerCase().includes(searchLower) || item.travel_type?.toLowerCase().includes(searchLower);
        if (!matches) return false;
      }
      if (mainViewMode === 'all_items' && quickFilter !== 'all' && item.status !== quickFilter) return false;
      if (activeDistributionFilter) {
        if (mainViewMode === 'all_items' || mainViewMode === 'par_statut') {
          if (activeDistributionFilter === 'other') {
            if (['completed', 'planned', 'cancelled'].includes(item.status)) return false;
          } else if (item.status !== activeDistributionFilter) return false;
        }
        if (mainViewMode === 'par_type') {
          if (item.type !== activeDistributionFilter && item.travel_type !== activeDistributionFilter) return false;
        }
        if (mainViewMode === 'par_indemnite') {
          const amount = parseFloat(isMissionTab ? item.total_allowance : item.allowance_amount) || 0;
          if (isMissionTab) {
            if (activeDistributionFilter === 'lt_500' && amount >= 500) return false;
            if (activeDistributionFilter === '500_2000' && (amount < 500 || amount >= 2000)) return false;
            if (activeDistributionFilter === '2000_5000' && (amount < 2000 || amount >= 5000)) return false;
            if (activeDistributionFilter === 'gt_5000' && amount < 5000) return false;
          } else {
            if (activeDistributionFilter === 'lt_300' && amount >= 300) return false;
            if (activeDistributionFilter === '300_1000' && (amount < 300 || amount >= 1000)) return false;
            if (activeDistributionFilter === '1000_3000' && (amount < 1000 || amount >= 3000)) return false;
            if (activeDistributionFilter === 'gt_3000' && amount < 3000) return false;
          }
        }
      }
      return true;
    });

    const filtered = applyColumnFilters(base, columnFilters, columnFilterRules);
    return [...filtered].sort((a, b) => {
      const dateA = new Date(getItemDate(a) || 0);
      const dateB = new Date(getItemDate(b) || 0);
      return dateB - dateA;
    });
  }, [currentData, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, isMissionTab, columnFilters, columnFilterRules]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredData);

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) return;
    try {
      const endpoint = isMissionTab ? 'missions' : 'travel';
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/salaries/missions-travel/${endpoint}/${id}`)));
      clearSelection();
      notifyBulkDeleted(showNotify, selectedRecords.length, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleViewDetails = (item) => {
    setDetailItem(item);
    setShowDetailsModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('common.delete_confirmation'))) return;

    try {
      const endpoint = activeTab === 'missions' ? 'missions' : 'travel';
      const result = await localApiAdapter.delete(`/salaries/missions-travel/${endpoint}/${id}`);

      if (result?.success === false) {
        notifyError(showNotify, result.error || p('save_error'));
        return;
      }
      notifyDeleted(showNotify, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      notifyError(showNotify, p('network_error'));
    }
  };
  
  // Modal handlers
  const emptyMissionForm = () => ({
    employeeId: '',
    titre: '',
    type: '',
    destination: '',
    dateDebut: '',
    dateFin: '',
    distance: '',
    forfaitJournalier: '',
    transport: '',
    autresIndemnites: '',
    montantIndemnite: '',
    typeVehicule: '',
    status: '',
    reason: '',
    notes: '',
  });

  const resetForm = () => {
    setShowModal(false);
    setEditingRecord(null);
    setFormTab('info');
    setFormData(emptyMissionForm());
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormTab('info');
    setFormData(emptyMissionForm());
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingRecord(item);
    if (isMissionTab) {
      setFormData({
        employeeId: item.employee_mat || item.employee_id || '',
        titre: item.title ?? '',
        type: item.type ?? '',
        destination: item.destination ?? '',
        dateDebut: item.start_date ?? '',
        dateFin: item.end_date ?? '',
        distance: '',
        forfaitJournalier: item.daily_rate != null && item.daily_rate !== '' ? String(item.daily_rate) : '',
        transport: item.transport_cost != null && item.transport_cost !== '' ? String(item.transport_cost) : '',
        autresIndemnites: item.other_costs != null && item.other_costs !== '' ? String(item.other_costs) : '',
        montantIndemnite: '',
        typeVehicule: '',
        status: item.status ?? '',
        reason: item.reason ?? '',
        notes: item.notes ?? '',
      });
    } else {
      setFormData({
        employeeId: item.employee_mat || item.employee_id || '',
        titre: '',
        type: item.travel_type ?? '',
        destination: item.destination ?? '',
        dateDebut: item.date ?? '',
        dateFin: '',
        distance: item.distance != null && item.distance !== '' ? String(item.distance) : '',
        forfaitJournalier: '',
        transport: '',
        autresIndemnites: '',
        montantIndemnite: item.allowance_amount != null && item.allowance_amount !== '' ? String(item.allowance_amount) : '',
        typeVehicule: item.reason ?? '',
        status: item.status ?? '',
        reason: item.reason ?? '',
        notes: item.notes ?? '',
      });
    }
    setFormTab('info');
    setShowModal(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeTab === 'missions') {
      if (!formData.employeeId?.trim() || !formData.titre?.trim() || !formData.type?.trim() || !formData.destination?.trim() || !formData.dateDebut || !formData.dateFin) {
        notifyError(showNotify, p('validation_mission_required'));
        return;
      }
    } else {
      if (!formData.employeeId?.trim() || !formData.type?.trim() || !formData.destination?.trim() || !formData.dateDebut) {
        notifyError(showNotify, p('validation_travel_required'));
        return;
      }
      if (formData.montantIndemnite === '' || formData.montantIndemnite === undefined) {
        notifyError(showNotify, p('validation_allowance_required'));
        return;
      }
    }

    try {
      if (activeTab === 'missions') {
        const missionBody = {
          employee_id: formData.employeeId,
          title: formData.titre,
          type: formData.type,
          destination: formData.destination,
          start_date: formData.dateDebut,
          end_date: formData.dateFin,
          daily_rate: parseFloat(formData.forfaitJournalier) || 0,
          transport_cost: parseFloat(formData.transport) || 0,
          other_costs: parseFloat(formData.autresIndemnites) || 0,
          status: formData.status || editingRecord?.status || 'planned',
          reason: formData.reason || '',
          notes: formData.notes || ''
        };

        const result = editingRecord
          ? await localApiAdapter.put(`/salaries/missions-travel/missions/${editingRecord.id}`, missionBody)
          : await localApiAdapter.post('/salaries/missions-travel/missions', missionBody);
        if (result?.success === false) {
          notifyError(showNotify, result.error || result.message || p('save_error'));
          return;
        }
      } else {
        const payload = {
          employee_id: formData.employeeId,
          date: formData.dateDebut,
          destination: formData.destination,
          distance: parseFloat(formData.distance) || 0,
          travel_type: formData.type,
          allowance_amount: parseFloat(formData.montantIndemnite) || 0,
          status: formData.status || editingRecord?.status || 'completed',
          reason: formData.typeVehicule || formData.reason || '',
          notes: formData.notes || ''
        };

        const result = editingRecord
          ? await localApiAdapter.put(`/salaries/missions-travel/travel/${editingRecord.id}`, payload)
          : await localApiAdapter.post('/salaries/missions-travel/travel', payload);
        if (result?.success === false) {
          notifyError(showNotify, result.error || result.message || p('save_error'));
          return;
        }
      }

      if (editingRecord) notifyUpdated(showNotify, entityLabel());
      else notifyCreated(showNotify, entityLabel());
      await fetchData();
      resetForm();
    } catch (err) {
      console.error(err);
      notifyError(showNotify, err.message || p('network_error'));
    }
  };

  const qawaemBaseColumns = useMemo(() => {
    if (isMissionTab) {
      return ensureStatusBeforeActions([
        { key: 'employee_name', label: p('col_employee'), required: true },
        { key: 'title', label: p('col_title') },
        { key: 'destination', label: p('col_destination') },
        { key: 'start_date', label: p('col_period') },
        { key: 'total_allowance', label: p('col_allowances') },
        { key: 'status', label: t('common.status') },
      ]);
    }
    return ensureStatusBeforeActions([
      { key: 'employee_name', label: p('col_employee'), required: true },
      { key: 'date', label: p('col_date') },
      { key: 'destination', label: p('col_destination') },
      { key: 'distance', label: p('col_distance') },
      { key: 'travel_type', label: p('col_type') },
      { key: 'allowance_amount', label: p('col_allowance') },
      { key: 'status', label: t('common.status') },
    ]);
  }, [isMissionTab, p, t]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('common.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Truck className="h-8 w-8 text-blue-400" />
            {isMissionTab ? p('title_missions') : p('title_travel')}
            <button type="button" onClick={fetchData} className="refresh-button" title={t('common.refresh')}><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">
            {isMissionTab ? p('subtitle_missions') : p('subtitle_travel')}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{t('common.advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
          >
            <Plus size={20} />
            <span>{isMissionTab ? p('add_mission') : p('add_travel')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('kpi_total_missions'), value: statsGlobal.totalMissions, icon: MapPin, color: 'from-blue-500 to-blue-600' },
          { title: p('kpi_total_travel'), value: statsGlobal.totalDeplacements, icon: Truck, color: 'from-purple-500 to-purple-600' },
          { title: p('kpi_in_progress'), value: statsGlobal.totalEnCours, icon: RefreshCcw, color: 'from-orange-500 to-orange-600' },
          { title: p('kpi_completed'), value: statsGlobal.totalTerminees, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('kpi_allowances_total'), value: formatAmountDisplay(statsGlobal.montantTotal), icon: DollarSign, color: 'from-yellow-500 to-yellow-600' },
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_items' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"><XCircle size={14} />{p('reset_filter')}</button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1">
          <button
            type="button"
            onClick={() => { setActiveTab('missions'); clearSelection(); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${activeTab === 'missions' ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
          >
            <ClipboardCheck className="h-5 w-5" />
            <span>{p('tab_missions')}</span>
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('deplacements'); clearSelection(); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${activeTab === 'deplacements' ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
          >
            <Truck className="h-5 w-5" />
            <span>{p('tab_travel')}</span>
          </button>
        </div>
      </div>

      <div className="glass-panel glass-table-shell overflow-visible">
        <SalairesListControlsStrip
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
          isDashboardLightMode={isDashboardLightMode}
          cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
          tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
          filterActions={renderSalairesBulkDeleteFilterAction({
            selectedCount: selectedRecords.length,
            onBulkDelete: handleBulkDelete,
            t,
          })}
        />

        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('employee_name') ? <TableColumnHeaderFilter {...colHeaderFilterProps('employee_name', p('col_employee'))} /> : null}
                  {isMissionTab ? (
                    <>
                      {showCol('title') ? <TableColumnHeaderFilter {...colHeaderFilterProps('title', p('col_title'))} /> : null}
                      {showCol('destination') ? <TableColumnHeaderFilter {...colHeaderFilterProps('destination', p('col_destination'))} /> : null}
                      {showCol('start_date') ? <TableColumnHeaderFilter {...colHeaderFilterProps('start_date', p('col_period'))} /> : null}
                      {showCol('total_allowance') ? <TableColumnHeaderFilter {...colHeaderFilterProps('total_allowance', p('col_allowances'))} /> : null}
                      {showCol('status') ? (
                      <TableColumnHeaderFilter
                        {...colHeaderFilterProps('status', t('common.status'), {
                          align: 'center',
                          kind: 'select',
                          options: statusOptions.map((o) => ({ value: o.value, label: o.label })),
                        })}
                      />
                      ) : null}
                    </>
                  ) : (
                    <>
                      {showCol('date') ? <TableColumnHeaderFilter {...colHeaderFilterProps('title', p('col_date'))} /> : null}
                      {showCol('destination') ? <TableColumnHeaderFilter {...colHeaderFilterProps('destination', p('col_destination'))} /> : null}
                      {showCol('distance') ? <TableColumnHeaderFilter {...colHeaderFilterProps('distance', p('col_distance'))} /> : null}
                      {showCol('travel_type') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_type')}</th> : null}
                      {showCol('allowance_amount') ? <TableColumnHeaderFilter {...colHeaderFilterProps('allowance_amount', p('col_allowance'))} /> : null}
                      {showCol('status') ? (
                      <TableColumnHeaderFilter
                        {...colHeaderFilterProps('status', t('common.status'), {
                          align: 'center',
                          kind: 'select',
                          options: statusOptions.map((o) => ({ value: o.value, label: o.label })),
                        })}
                      />
                      ) : null}
                    </>
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12"><RefreshCcw className="h-12 w-12 text-white/30 mx-auto mb-3 animate-spin" /><p className="text-white/50">{t('common.loading')}</p></td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12"><Info className="h-12 w-12 text-white/30 mx-auto mb-3" /><p className="text-white/50 text-lg">{isMissionTab ? p('empty_mission') : p('empty_travel')}</p></td></tr>
                ) : (
                  filteredData.map((item, index) => (
                    <tr key={item.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(item.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(item.id)}
                        onChange={() => handleSelectRecord(item.id)}
                      />
                      {showCol('employee_name') ? (
                      <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{item.employee_name}</span><span className="text-white/50 text-xs mt-0.5">{item.employee_mat}</span></div></td>
                      ) : null}
                      {isMissionTab ? (
                        <>
                          {showCol('title') ? (
                          <td className="px-6 py-4"><span className="text-white/70 text-sm">{item.title}</span></td>
                          ) : null}
                          {showCol('destination') ? (
                          <td className="px-6 py-4"><span className="text-white/70 text-sm">{item.destination}</span></td>
                          ) : null}
                          {showCol('start_date') ? (
                          <td className="px-6 py-4"><span className="text-white/70 text-sm">{fmtDate(item.start_date)} → {fmtDate(item.end_date)}</span></td>
                          ) : null}
                          {showCol('total_allowance') ? (
                          <td className="px-6 py-4"><span className="text-white font-bold text-lg">{formatAmountDisplay(item.total_allowance)}</span></td>
                          ) : null}
                          {showCol('status') ? (
                          <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={item.status} recordId={item.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })} /></div></td>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {showCol('date') ? (
                          <td className="px-6 py-4"><span className="text-white/70 text-sm">{fmtDate(item.date)}</span></td>
                          ) : null}
                          {showCol('destination') ? (
                          <td className="px-6 py-4"><span className="text-white/70 text-sm">{item.destination}</span></td>
                          ) : null}
                          {showCol('distance') ? (
                          <td className="px-6 py-4"><span className="text-white/70 text-sm">{item.distance} km</span></td>
                          ) : null}
                          {showCol('travel_type') ? (
                          <td className="px-6 py-4"><span className="text-white/70 text-sm">{item.travel_type}</span></td>
                          ) : null}
                          {showCol('allowance_amount') ? (
                          <td className="px-6 py-4"><span className="text-white font-bold text-lg">{formatAmountDisplay(item.allowance_amount)}</span></td>
                          ) : null}
                          {showCol('status') ? (
                          <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={item.status} recordId={item.id} options={statusOptions} onChange={handleStatusChange} title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })} /></div></td>
                          ) : null}
                        </>
                      )}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleViewDetails(item)}
                          onEdit={() => handleEdit(item)}
                          onDelete={() => handleDelete(item.id)}
                          viewTitle={t('common.view_details')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{isMissionTab ? p('footer_total_missions', { count: filteredData.length }) : p('footer_total_travel', { count: filteredData.length })}</span>
                      {isMissionTab ? (
                        <span>{p('footer_allowances_total')} <span className="text-white font-semibold">{formatAmountDisplay(filteredData.reduce((sum, item) => sum + (parseFloat(item.total_allowance) || 0), 0))}</span></span>
                      ) : (
                        <>
                          <span>{p('footer_distance_total')} <span className="text-white font-semibold">{filteredData.reduce((sum, item) => sum + (parseFloat(item.distance) || 0), 0).toFixed(1)} km</span></span>
                          <span>{p('footer_allowances_total')} <span className="text-white font-semibold">{formatAmountDisplay(filteredData.reduce((sum, item) => sum + (parseFloat(item.allowance_amount) || 0), 0))}</span></span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {loading ? (
              <div className="col-span-full text-center py-12"><RefreshCcw className="animate-spin h-10 w-10 text-white/50 mx-auto mb-4" /><p className="text-white/50">{t('common.loading')}</p></div>
            ) : filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">{isMissionTab ? <ClipboardCheck size={20} className="text-blue-400" /> : <Truck size={20} className="text-purple-400" />}{isMissionTab ? item.title : fmtDate(item.date)}</h3>
                        <QawaemClickableStatusBadge status={item.status} recordId={item.id} options={statusOptions} onChange={handleStatusChange} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_employee')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2"><User size={14} className="text-blue-400 shrink-0" /><span>{item.employee_name}</span></div>
                            <div><span className="font-medium">{p('label_matricule')}</span> {item.employee_mat}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{isMissionTab ? p('card_mission') : p('card_travel')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2"><MapPin size={14} className="text-amber-400 shrink-0" /><span>{item.destination}</span></div>
                            {isMissionTab ? (
                              <>
                                <div><span className="font-medium">{p('label_type')}</span> {item.type}</div>
                                <div><span className="font-medium">{p('label_period')}</span> {fmtDate(item.start_date)} → {fmtDate(item.end_date)}</div>
                              </>
                            ) : (
                              <>
                                <div><span className="font-medium">{p('label_type')}</span> {item.travel_type}</div>
                                <div><span className="font-medium">{p('label_distance')}</span> {item.distance} km</div>
                              </>
                            )}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_allowances')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium text-green-400">{p('label_amount')}</span> <span className="text-white font-bold text-lg">{formatAmountDisplay(isMissionTab ? item.total_allowance : item.allowance_amount)}</span></div>
                            {!isMissionTab && item.reason ? <div><span className="font-medium">{p('label_vehicle')}</span> {item.reason}</div> : null}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {isMissionTab && item.reason ? <div><span className="font-medium">{p('label_reason')}</span> {item.reason}</div> : null}
                            {item.notes ? <div><span className="font-medium">{p('label_notes')}</span> {item.notes}</div> : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleViewDetails(item)}
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      viewTitle={t('common.view_details')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12"><Truck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{isMissionTab ? p('empty_mission') : p('empty_travel')}</p></div>
            )}
          </div>
        )}
      </div>

      {showDetailsModal && detailItem ? (
      <QawaemErpViewModal
        open={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setDetailItem(null); }}
        title={p('details_title')}
        icon={Eye}
        testId="missions-view-modal"
      >
          <div className="space-y-4 text-white">
            <div>
              <p className="text-white/60 text-sm">{p('col_employee')}</p>
              <p className="font-semibold">{detailItem.employee_name}</p>
              <p className="text-white/50 text-sm">{detailItem.employee_mat}</p>
            </div>
            <div>
              <p className="text-white/60 text-sm">{p('col_destination')}</p>
              <p>{detailItem.destination}</p>
            </div>
            {activeTab === 'missions' && (
              <>
                <div>
                  <p className="text-white/60 text-sm">{p('label_title')}</p>
                  <p>{detailItem.title}</p>
                </div>
                {detailItem.reason ? (
                  <div>
                    <p className="text-white/60 text-sm">{p('label_reason')}</p>
                    <p>{detailItem.reason}</p>
                  </div>
                ) : null}
                {detailItem.notes ? (
                  <div>
                    <p className="text-white/60 text-sm">{p('label_comment')}</p>
                    <p>{detailItem.notes}</p>
                  </div>
                ) : null}
              </>
            )}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 rounded-xl text-white transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? (activeTab === 'missions' ? p('modal_edit_mission') : p('modal_edit_travel')) : (activeTab === 'missions' ? p('modal_new_mission') : p('modal_new_travel'))}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="mission-deplacement-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('update') : p('create')}</button>
                <button type="button" onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <QawaemErpModalFormTabBar tabs={missionFormTabs} activeTab={formTab} onTabChange={setFormTab} isLight={isDashboardLightMode} ariaLabel={editingRecord ? (activeTab === 'missions' ? p('modal_edit_mission') : p('modal_edit_travel')) : (activeTab === 'missions' ? p('modal_new_mission') : p('modal_new_travel'))} />
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="mission-deplacement-form" onSubmit={handleSubmit}>
                {formTab === 'info' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('employee_required')} required /></label><CustomSelect value={formData.employeeId} onChange={(value) => setFormData({ ...formData, employeeId: value })} options={employees.map((emp) => ({ value: String(emp.id ?? emp.mat), label: `${emp.prenom || ''} ${emp.nom || ''}`.trim() + (emp.mat ? ` (${emp.mat})` : '') }))} placeholder={p('employee_placeholder')} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('type_required')} required /></label><CustomSelect value={formData.type} onChange={(value) => setFormData({ ...formData, type: value })} options={isMissionTab ? MISSION_TYPE_OPTIONS : DEPLACEMENT_TYPE_OPTIONS} placeholder={p('type_placeholder')} autoSelect={false} searchable creatable /></div>
                    {isMissionTab && (<div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('mission_title_required')} required /></label><CustomSelect value={formData.titre} onChange={(value) => setFormData({ ...formData, titre: value })} options={[]} placeholder={p('mission_title_placeholder')} autoSelect={false} searchable creatable /></div>)}
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('destination_required')} required /></label><CustomSelect value={formData.destination} onChange={(value) => { const perDiem = getPerDiemRate(value); setFormData({ ...formData, destination: value, forfaitJournalier: isMissionTab && perDiem ? String(perDiem) : formData.forfaitJournalier }); }} options={DESTINATION_OPTIONS} placeholder={p('destination_placeholder')} autoSelect={false} searchable creatable /></div>
                    {!isMissionTab && (<div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('distance_label')}</label><CustomNumberInput step={0.1} decimals={1} value={formData.distance} onChange={(v) => setFormData({ ...formData, distance: v })} placeholder="150" className="w-full" /></div>)}
                    {isMissionTab ? (<><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('date_start_required')} required /></label><CustomDatePicker value={formData.dateDebut} onChange={(v) => setFormData({ ...formData, dateDebut: v })} placeholder={p('select')} /></div><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('date_end_required')} required /></label><CustomDatePicker value={formData.dateFin} onChange={(v) => setFormData({ ...formData, dateFin: v })} placeholder={p('select')} minDate={formData.dateDebut || undefined} /></div></>) : (<><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('date_required')} required /></label><CustomDatePicker value={formData.dateDebut} onChange={(v) => setFormData({ ...formData, dateDebut: v })} placeholder={p('select')} /></div><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('vehicle_type_label')}</label><CustomSelect value={formData.typeVehicule} onChange={(value) => setFormData({ ...formData, typeVehicule: value })} options={[]} placeholder={p('vehicle_placeholder')} autoSelect={false} searchable creatable /></div></>)}
                  </div>
                )}
                {formTab === 'details' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    {isMissionTab ? (<><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('daily_rate_label')}</label><CustomNumberInput step={0.01} decimals={2} value={formData.forfaitJournalier} onChange={(v) => setFormData({ ...formData, forfaitJournalier: v })} placeholder="800" className="w-full" /></div><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('transport_label')}</label><CustomNumberInput step={0.01} decimals={2} value={formData.transport} onChange={(v) => setFormData({ ...formData, transport: v })} placeholder="200" className="w-full" /></div><div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('other_allowances_label')}</label><CustomNumberInput step={0.01} decimals={2} value={formData.autresIndemnites} onChange={(v) => setFormData({ ...formData, autresIndemnites: v })} placeholder="100" className="w-full" /></div></>) : (<div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('allowance_amount_required')} required /></label><CustomNumberInput required step={0.01} decimals={2} value={formData.montantIndemnite} onChange={(v) => setFormData({ ...formData, montantIndemnite: v })} placeholder="500" className="w-full" /></div>)}
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('status_label')}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={(isMissionTab ? missionStatusOptions : deplacementStatusOptions).map((s) => ({ value: s.value, label: s.label }))} placeholder={p('status_placeholder')} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{isMissionTab ? p('reason_label') : p('reason_vehicle_label')}</label><CustomSelect value={isMissionTab ? formData.reason : formData.typeVehicule} onChange={(value) => setFormData(isMissionTab ? { ...formData, reason: value } : { ...formData, typeVehicule: value, reason: value })} options={[]} placeholder={isMissionTab ? p('reason_mission_placeholder') : p('vehicle_placeholder')} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('notes_label')}</label><CustomSelect value={formData.notes} onChange={(value) => setFormData({ ...formData, notes: value })} options={[]} placeholder={p('notes_placeholder')} autoSelect={false} searchable creatable /></div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default MissionsDeplacementsPage;


